var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useTranslation } from 'react-i18next';
import { useMutation } from '@shared/lib/hooks';
import { ToastService } from '@shared/lib/services/toast.service';
import { SolutionService } from '../services/validation.service';
import { validationKeys } from './keys';
/**
 * Edit mutations for a single solution. Each invalidates the solution detail (auto, via
 * `options.queryKey`) and the parent file query (so the left-panel name/system chips refresh).
 * Server-side validation errors surface as a toast.
 */
export function useSolutionMutations(fileId, solutionId) {
    const { t } = useTranslation();
    const solutionKey = validationKeys.solution(fileId, solutionId);
    const fileKey = validationKeys.file(fileId);
    const fail = (err) => {
        ToastService.error((err === null || err === void 0 ? void 0 : err.msg) || t('Could not save changes. Please try again.'));
        throw err;
    };
    const saved = (queryClient) => {
        queryClient.invalidateQueries({ queryKey: fileKey });
        ToastService.success(t('Changes saved'));
    };
    const name = useMutation({
        mutationFn: value => SolutionService.updateName(fileId, solutionId, value).catch(fail),
        onSuccess: saved,
        options: { queryKey: solutionKey }
    });
    const description = useMutation({
        mutationFn: (_a) => {
            var { description: value } = _a, target = __rest(_a, ["description"]);
            return SolutionService.updateDescription(fileId, solutionId, value, target).catch(fail);
        },
        onSuccess: saved,
        options: { queryKey: solutionKey }
    });
    const systems = useMutation({
        mutationFn: value => SolutionService.updateSystems(fileId, solutionId, value).catch(fail),
        onSuccess: saved,
        options: { queryKey: solutionKey }
    });
    const parameters = useMutation({
        mutationFn: payload => SolutionService.updateParameters(fileId, solutionId, payload).catch(fail),
        onSuccess: saved,
        options: { queryKey: solutionKey }
    });
    const bom = useMutation({
        mutationFn: value => SolutionService.updateBom(fileId, solutionId, value).catch(fail),
        onSuccess: saved,
        options: { queryKey: solutionKey }
    });
    return {
        updateName: name.mutate,
        isSavingName: name.isLoading,
        updateDescription: description.mutate,
        isSavingDescription: description.isLoading,
        updateSystems: systems.mutate,
        isSavingSystems: systems.isLoading,
        updateParameters: parameters.mutate,
        isSavingParameters: parameters.isLoading,
        updateBom: bom.mutate,
        isSavingBom: bom.isLoading
    };
}
export function useDeleteSolution(fileId) {
    const { t } = useTranslation();
    const del = useMutation({
        mutationFn: solutionId => SolutionService.remove(fileId, solutionId).catch((err) => {
            ToastService.error(t('Failed to delete solution'));
            throw err;
        }),
        onSuccess: () => ToastService.success(t('Solution deleted')),
        options: { queryKey: validationKeys.file(fileId) }
    });
    return { deleteSolution: del.mutate, isDeleting: del.isLoading };
}
